import { useMemo } from 'react'

import type { PriceHistoryPointDto } from '../api/marketAnalytics'
import { formatAnalyticsDateTime, formatAnalyticsRub } from '../screens/marketAnalyticsFormat'

type PriceHistoryChartProps = {
  points: PriceHistoryPointDto[]
  loading?: boolean
  error?: string | null
}

export const PriceHistoryChart = ({ points, loading = false, error = null }: PriceHistoryChartProps) => {
  const chart = useMemo(() => {
    if (points.length === 0) {
      return null
    }
    const width = 520
    const height = 140
    const padX = 12
    const padY = 16
    const prices = points.map((point) => point.price)
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    const span = max - min || Math.max(max * 0.05, 1)
    const innerW = width - padX * 2
    const innerH = height - padY * 2
    const coords = points.map((point, index) => {
      const x =
        points.length === 1
          ? padX + innerW / 2
          : padX + (index / (points.length - 1)) * innerW
      const y = padY + innerH - ((point.price - min) / span) * innerH
      return { x, y, point }
    })
    const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ')
    const first = points[0]
    const last = points[points.length - 1]
    return { width, height, path, coords, min, max, first, last }
  }, [points])

  if (loading) {
    return <p className="echat-muted market-analytics-history__status">Загрузка графика…</p>
  }
  if (error) {
    return (
      <p className="echat-error market-analytics-history__status" role="alert">
        {error}
      </p>
    )
  }
  if (!chart) {
    return <p className="echat-muted market-analytics-history__status">Нет точек истории по этой позиции.</p>
  }

  return (
    <div className="market-analytics-history">
      <div className="market-analytics-history__meta">
        <span>
          {formatAnalyticsRub(chart.first.price)} → {formatAnalyticsRub(chart.last.price)}
        </span>
        <span className="echat-muted">
          {formatAnalyticsDateTime(chart.first.observed_at)} —{' '}
          {formatAnalyticsDateTime(chart.last.observed_at)} · {points.length} точ.
        </span>
      </div>
      <svg
        className="market-analytics-history__svg"
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        role="img"
        aria-label="График изменения цены"
      >
        <line
          className="market-analytics-history__grid"
          x1="12"
          x2={chart.width - 12}
          y1={chart.height / 2}
          y2={chart.height / 2}
        />
        <path className="market-analytics-history__line" d={chart.path} />
        {chart.coords.map((coord) => (
          <circle
            key={`${coord.point.observed_at}-${coord.point.price}`}
            className="market-analytics-history__dot"
            cx={coord.x}
            cy={coord.y}
            r={3.2}
          >
            <title>
              {formatAnalyticsRub(coord.point.price)} · {formatAnalyticsDateTime(coord.point.observed_at)}
            </title>
          </circle>
        ))}
      </svg>
      <div className="market-analytics-history__scale echat-muted">
        <span>min {formatAnalyticsRub(chart.min)}</span>
        <span>max {formatAnalyticsRub(chart.max)}</span>
      </div>
    </div>
  )
}
